import { useEffect, useState } from 'react';
import { useTraccarMessage } from './useTraccarSocket';
import { useFleetStore } from '@/stores/fleet';
import { translateEvent } from '@/lib/traccarLabels';

const PERMISSION_KEY = 'fismatek-notif-enabled';

/**
 * Notification API local do navegador. Sem service worker / web push;
 * o usuário precisa estar com a aba aberta para ver as notificações.
 *
 * Estados:
 * - 'default': nunca pediu (não notifica)
 * - 'granted' + flag local: notifica eventos do WebSocket
 * - 'denied': bloqueado pelo usuário
 */

export type NotifState = 'idle' | 'granted' | 'denied' | 'unsupported';

function readEnabled(): boolean {
  try {
    return window.localStorage.getItem(PERMISSION_KEY) === 'true';
  } catch {
    return false;
  }
}

function notify(title: string, body: string) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/fismatek-icon.png', tag: title });
  } catch {
    /* ignora */
  }
}

/** Hook controlador de permissão e estado de push. */
export function useNotificationPermission() {
  const [state, setState] = useState<NotifState>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted' && readEnabled()) return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return 'idle';
  });

  const request = async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      window.localStorage.setItem(PERMISSION_KEY, 'true');
      setState('granted');
      notify('Notificações ativadas', 'Você receberá alertas dos eventos das suas máquinas.');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    const result = await Notification.requestPermission();
    if (result === 'granted') {
      window.localStorage.setItem(PERMISSION_KEY, 'true');
      setState('granted');
      notify('Notificações ativadas', 'Você receberá alertas dos eventos das suas máquinas.');
    } else if (result === 'denied') {
      setState('denied');
    }
  };

  const disable = () => {
    window.localStorage.setItem(PERMISSION_KEY, 'false');
    setState('idle');
  };

  return { state, request, disable };
}

/**
 * Dispara notificação nativa do navegador para cada evento que chega via
 * WebSocket, exceto os que já são tratados em useGeofenceAutoBlock
 * (geofenceEnter/geofenceExit).
 */
export function usePushNotifications() {
  useTraccarMessage((msg) => {
    if (!msg.events?.length) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (!readEnabled()) return;

    const devices = useFleetStore.getState().devices;
    for (const event of msg.events) {
      // Geofence events são notificados pelo useGeofenceAutoBlock
      if (event.type === 'geofenceEnter' || event.type === 'geofenceExit') continue;
      const deviceLabel = event.deviceId != null
        ? devices.get(event.deviceId)?.name ?? `Máquina ${event.deviceId}`
        : 'Sistema';
      notify(translateEvent(event.type), deviceLabel);
    }
  });
}

/**
 * Verifica manutenções por data e dispara notificação se vencidas ou próximas.
 * Roda uma vez ao montar e a cada 1h.
 */
export function useMaintenanceAlerts(maintenanceList: { name: string; type: string; attributes?: Record<string, unknown> }[]) {
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!readEnabled()) return;

    const seen = new Set<string>();

    const check = () => {
      const today = new Date();
      const todayKey = today.toISOString().slice(0, 10);

      for (const m of maintenanceList) {
        if (m.type !== 'schedule') continue;
        const attrs = m.attributes ?? {};
        const dateStr = typeof attrs.scheduleDate === 'string' ? (attrs.scheduleDate as string) : null;
        if (!dateStr) continue;

        const target = new Date(`${dateStr}T23:59:59`);
        const diffDays = Math.floor((target.getTime() - today.getTime()) / (24 * 3_600_000));

        let title: string | null = null;
        if (diffDays < 0) title = 'Manutenção vencida';
        else if (diffDays <= 7) title = 'Manutenção próxima';

        if (title) {
          const dedupKey = `${todayKey}:${m.name}:${dateStr}`;
          if (seen.has(dedupKey)) continue;
          seen.add(dedupKey);
          notify(title, `${m.name} (${new Date(`${dateStr}T00:00:00`).toLocaleDateString('pt-BR')})`);
        }
      }
    };

    check();
    const id = setInterval(check, 60 * 60 * 1000); // 1h
    return () => clearInterval(id);
  }, [maintenanceList]);
}
